import {
  NAV_LINK_BG,
  NAV_LINK_ICON,
  NAV_LINK_SELECTED_ICON,
  NAV_LINK_SELECTED_TEXT,
  NAV_LINK_TEXT,
  NAV_LINK_TINT_BG,
} from "@funded-here-interface/common/src/constant/color";
import {
  FONT_FAMILY,
  NAV_LINK_FONT_SIZE,
} from "@funded-here-interface/common/src/constant/typography";
import { createStyles } from "@mantine/emotion";

// Styling mirrors investor-onboarding's NavBar.styles.ts so both apps share one
// sidebar look (white rounded panel + mint NAV_LINK palette). The investor file
// additionally carries a mobile-Drawer (`embedded`) variant; admin has no drawer
// navbar, so those classes are intentionally omitted here.
export const useStyles = createStyles(() => ({
  sideBarContainer: {
    position: "fixed",
    height: "100vh",
    backgroundColor: "#FFFFFF",
    width: 240,
    display: "flex",
    flexDirection: "column",
    fontFamily: FONT_FAMILY,
    // Left corners only: the sidebar sits flush against the page area on the
    // right, so rounding that side would cut a notch into the background.
    borderRadius: "0px 24px 24px 0px",
    // Without this the rounded corners are invisible: the sidebar and the page
    // area behind it are both white, so the curve reveals more white with
    // nothing to contrast against.
    border: "1px solid #edf0f2",
    borderRight: "none",
    boxShadow: "0 4px 14px rgba(16, 24, 40, 0.08), 0 1px 3px rgba(16, 24, 40, 0.06)",
    // border-radius alone doesn't clip children — without this, the active nav
    // item's mint pill can render past the rounded corner.
    overflow: "hidden",
  },
  iconbox: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "32px 20px 32px 30px",
  },
  logo: {
    width: "70%",
  },
  link: {
    textDecoration: "none",
    color: NAV_LINK_TEXT,
    backgroundColor: NAV_LINK_BG,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    // Explicit so hover and active tints paint the same-width pill — Mantine's
    // own NavLink base styles don't guarantee width:100% here.
    width: "100%",
    boxSizing: "border-box",
    marginBottom: 6,

    // Mantine's NavLink declares `font-size: var(--mantine-font-size-sm)` on
    // the label element, so a size on the root above is overridden rather than
    // inherited and the label silently tracks the theme instead. It has to be
    // set here. Pinned by e2e/admin/navbar-label-size.spec.ts.
    "& .mantine-NavLink-label": {
      color: NAV_LINK_TEXT,
      fontSize: NAV_LINK_FONT_SIZE,
    },
    // Mantine 9's NavLink shares one "section" class for both leftSection (the
    // icon) and rightSection (a submenu chevron), told apart by data-position.
    "& .mantine-NavLink-section[data-position='left']": {
      color: NAV_LINK_ICON,
    },

    // Hover only tints the background — icon/text stay the resting slate colour.
    "&:hover:hover": {
      backgroundColor: NAV_LINK_TINT_BG,
    },

    "&[data-active][data-active]": {
      backgroundColor: NAV_LINK_TINT_BG,
      "& .mantine-NavLink-label": {
        color: NAV_LINK_SELECTED_TEXT,
        fontWeight: 600,
      },
      "& .mantine-NavLink-section": {
        color: NAV_LINK_SELECTED_ICON,
      },
    },
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    margin: "0 20px 0 12px",
  },
  // Mirrors Mantine's NavLink box metrics so this identity row and the Logout
  // NavLink below it share one icon and label baseline.
  personItem: {
    display: "flex",
    alignItems: "center",
    padding: "8px var(--mantine-spacing-sm)",
    gap: "var(--mantine-spacing-sm)",
    color: NAV_LINK_TEXT,
  },
  personIcon: {
    color: NAV_LINK_ICON,
    flexShrink: 0,
  },
  footer: {
    flexShrink: 0,
    margin: "24px 20px 16px 12px",
    color: NAV_LINK_TEXT,
  },
}));
